import React, { useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Wheel entry keys for translation and colors
const WHEEL_ENTRY_KEYS = [
  { key: 'namePicker', color: '#3B82F6' },
  { key: 'yesOrNo', color: '#10B981' },
  { key: 'teamGenerator', color: '#8B5CF6' },
  { key: 'numberWheel', color: '#F59E0B' },
  { key: 'letterWheel', color: '#EC4899' },
  { key: 'colorWheel', color: '#06B6D4' },
  { key: 'foodPicker', color: '#EF4444' },
  { key: 'moviePicker', color: '#6366F1' },
  { key: 'gamePicker', color: '#14B8A6' },
  { key: 'giveaway', color: '#F97316' },
  { key: 'truthOrDare', color: '#A855F7' },
  { key: 'question', color: '#0EA5E9' },
  { key: 'casino', color: '#DC2626' },
  { key: 'datePicker', color: '#059669' },
  { key: 'country', color: '#2563EB' },
  { key: 'drinkingGame', color: '#D97706' },
  { key: 'musicPicker', color: '#7C3AED' },
  { key: 'sports', color: '#16A34A' },
  { key: 'activity', color: '#DB2777' },
  { key: 'random', color: '#6B7280' }
];

const AnimatedHeroWheel = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const rotationRef = useRef(0);
  const animationRef = useRef(null);
  const { t } = useLanguage();

  // Get translated wheel entries
  const getWheelEntries = () => {
    return WHEEL_ENTRY_KEYS.map(({ key, color }) => ({
      text: t(`animatedWheel.labels.${key}`) || key,
      color
    }));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const wheelEntries = getWheelEntries();
      const rect = canvas.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      // Ensure radius is never negative (minimum 10px)
      const radius = Math.max(10, Math.min(centerX, centerY) - 10);
      
      // Clear
      ctx.clearRect(0, 0, rect.width, rect.height);
      
      // Draw shadow
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.restore();
      
      // Draw wheel slices
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(rotationRef.current);
      
      const sliceAngle = (Math.PI * 2) / wheelEntries.length;
      
      wheelEntries.forEach((entry, i) => {
        const startAngle = i * sliceAngle;
        const endAngle = startAngle + sliceAngle;
        
        // Draw slice
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = entry.color;
        ctx.fill();
        
        // Draw border
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw text
        ctx.save();
        ctx.rotate(startAngle + sliceAngle / 2);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${Math.max(10, radius * 0.07)}px system-ui, -apple-system, sans-serif`;
        
        // Add text shadow for readability
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        
        ctx.fillText(entry.text, radius - 15, 0);
        ctx.restore();
      });
      
      ctx.restore();
      
      // Draw center circle
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius * 0.15);
      gradient.addColorStop(0, '#FFFFFF');
      gradient.addColorStop(0.7, '#F8FAFC');
      gradient.addColorStop(1, '#E2E8F0');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.15, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = '#CBD5E1';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw pointer (static, at LEFT side pointing RIGHT towards the wheel center)
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // Pointer on LEFT side, pointing RIGHT (towards wheel center)
      // The tip should point INWARD (towards center = RIGHT direction when at left side)
      const pointerSize = radius * 0.12;
      ctx.beginPath();
      // Triangle with tip pointing RIGHT (towards center of wheel)
      // Starting from left edge of wheel, tip goes towards center
      ctx.moveTo(-radius + pointerSize + 5, 0);  // Tip pointing right (towards center)
      ctx.lineTo(-radius - 5, -pointerSize / 2);  // Top left corner (outside wheel)
      ctx.lineTo(-radius - 5, pointerSize / 2);   // Bottom left corner (outside wheel)
      ctx.closePath();
      
      ctx.fillStyle = '#EF4444';
      ctx.fill();
      ctx.strokeStyle = '#B91C1C';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.restore();
    };

    // Animation loop - slow continuous rotation
    const animate = () => {
      rotationRef.current += 0.003; // Slow rotation speed
      draw();
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [t]);

  return (
    <div className={`animated-hero-wheel ${className}`}>
      <canvas 
        ref={canvasRef} 
        className="hero-wheel-canvas"
        aria-label="Animated wheel showcasing all 20 wheel variants"
      />
    </div>
  );
};

export default AnimatedHeroWheel;
